import React, { useMemo, useCallback } from 'react';
import { useDragOverlay } from '../drag-overlay-provider';
import { getVisibleFrames } from '../utils/get-visible-frames';
import {
  useFps,
  useSelectedItems,
  useTimelineContext,
  useTransitions,
  useWriteContext,
} from '../utils/use-context';
import { deleteTransitions } from '../state/actions/delete-transition';
import { TimelineTransitionItem } from './timeline-transition-item';
import { TimelineTrackAndLayout } from './utils/drag/calculate-track-heights';
import { Z_INDEX_TRANSITION_MARKERS } from '../z-indices';

interface TimelineTransitionMarkersProps {
  timelineWidth: number;
  tracksLayout: TimelineTrackAndLayout[];
}

const TimelineTransitionMarkersUnmemoized: React.FC<
  TimelineTransitionMarkersProps
> = ({ timelineWidth, tracksLayout }) => {
  const { transitions } = useTransitions();
  const { durationInFrames } = useTimelineContext();
  const { fps } = useFps();
  const { selectedItems } = useSelectedItems();
  const timelineWriteContext = useWriteContext();
  const { isDragging, transitionsMeta } = useDragOverlay();

  // 드래그 중인 트랜지션 ID Set
  const draggingTransitionIds = useMemo(() => {
    if (!isDragging || transitionsMeta.length === 0) {
      return new Set<string>();
    }
    return new Set(transitionsMeta.map((meta) => meta.id));
  }, [isDragging, transitionsMeta]);

  const visibleFrames = useMemo(
    () =>
      getVisibleFrames({
        fps,
        totalDurationInFrames: durationInFrames,
      }),
    [fps, durationInFrames],
  );

  // 트랜지션 클릭 핸들러
  const handleTransitionClick = useCallback(
    (e: React.MouseEvent, transitionId: string) => {
      const isMultiSelect = e.metaKey || e.ctrlKey;

      timelineWriteContext.setState({
        update: (state) => {
          if (isMultiSelect) {
            // 다중 선택: 이미 선택된 경우 해제, 아니면 추가
            const isAlreadySelected =
              state.selectedItems.includes(transitionId);
            return {
              ...state,
              selectedItems: isAlreadySelected
                ? state.selectedItems.filter((id) => id !== transitionId)
                : [...state.selectedItems, transitionId],
            };
          } else {
            // 단일 선택
            return {
              ...state,
              selectedItems: [transitionId],
            };
          }
        },
        commitToUndoStack: false,
      });
    },
    [timelineWriteContext],
  );

  // 트랜지션 삭제 핸들러
  const handleTransitionDelete = useCallback(
    (transitionId: string) => {
      timelineWriteContext.setState({
        update: (state) => deleteTransitions(state, [transitionId]),
        commitToUndoStack: true,
      });
    },
    [timelineWriteContext],
  );

  // 드래그 중인 트랜지션은 타임라인에서 숨기고, 나머지는 그대로 표시
  // (드래그 중인 트랜지션은 drag-overlay에서 커서를 따라다니며 표시됨)
  const transitionList = useMemo(() => {
    const baseTransitions = Object.values(transitions);

    // 드래그 중인 트랜지션 제외
    const filteredTransitions = baseTransitions.filter(
      (transition) => !draggingTransitionIds.has(transition.id),
    );

    return filteredTransitions;
  }, [transitions, draggingTransitionIds]);

  if (transitionList.length === 0) {
    return null;
  }

  return (
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex: Z_INDEX_TRANSITION_MARKERS }}
    >
      {transitionList.map((transition) => (
        <TimelineTransitionItem
          key={transition.id}
          transition={transition}
          timelineWidth={timelineWidth}
          visibleFrames={visibleFrames}
          tracksLayout={tracksLayout}
          isSelected={selectedItems.includes(transition.id)}
          onClick={handleTransitionClick}
          onDelete={handleTransitionDelete}
        />
      ))}
    </div>
  );
};

export const TimelineTransitionMarkers = React.memo(
  TimelineTransitionMarkersUnmemoized,
);
